import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useSiteSettings } from "../lib/site-settings-context";

/**
 * Captures `?ref=CODE` query parameters anywhere on the site, persists the
 * code in localStorage for 30 days, and pings the backend so the click is
 * counted in the affiliate's dashboard. Mounts once at app root.
 *
 * Why 30 days? Aligns with industry-standard attribution windows (Amazon
 * Associates uses 24h; most SaaS partners use 30–90 days). Long enough for a
 * realistic buying cycle, short enough that we don't pay out on stale clicks.
 */
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const STORAGE_KEY = "cardlon_aff_ref";
const STORAGE_META = "cardlon_aff_ref_meta";

function setRef(code) {
  try {
    localStorage.setItem(STORAGE_KEY, code);
    localStorage.setItem(
      STORAGE_META,
      JSON.stringify({ code, ts: Date.now() }),
    );
  } catch (e) {
    /* localStorage disabled — silently ignore */
  }
}

function getRefAge() {
  try {
    const meta = JSON.parse(localStorage.getItem(STORAGE_META) || "null");
    if (!meta) return null;
    return { code: meta.code, ageMs: Date.now() - meta.ts };
  } catch (e) {
    return null;
  }
}

function clearRef() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_META);
  } catch (e) { /* ignore */ }
}

export function AffiliateTracker() {
  const location = useLocation();
  const settings = useSiteSettings();
  const enabled = !!settings?.affiliate_enabled;

  // Expire stale refs on first mount
  useEffect(() => {
    const existing = getRefAge();
    if (existing && existing.ageMs > REF_TTL_MS) {
      clearRef();
    }
  }, []);

  // If admin disables the program, clear any stored ref so checkouts don't
  // continue attributing to inactive codes.
  useEffect(() => {
    if (settings && !enabled) clearRef();
  }, [enabled, settings]);

  // On every route change, look for a ?ref= param
  useEffect(() => {
    if (!enabled) return; // master switch off — skip everything
    const params = new URLSearchParams(location.search);
    const code = (params.get("ref") || "").trim().toUpperCase();
    if (!code || code.length > 32) return;
    // Persist
    setRef(code);
    // Ping (best-effort)
    api
      .post("/affiliate/track", {
        code,
        landing_path: location.pathname,
        referrer: document.referrer || "",
      })
      .catch(() => {
        /* swallow — tracking must never break navigation */
      });
  }, [enabled, location.search, location.pathname]);

  return null;
}

export default AffiliateTracker;
